import React, { useEffect, useState } from 'react';
import { logger } from '@/utils/logger';
import { useTranslation } from 'react-i18next';
import { 
  getAppSettings, 
  setMinOrderAmount, 
  setPaymentMethodsText,
  savePaymentMethod, 
  deletePaymentMethod,
  setGoogleAnalyticsId,
  PaymentMethod 
} from '@/api/settings';
import { setOrderConfirmationText } from '@/utils/settings';
import { PlusCircle, Trash2, MoveUp, MoveDown, X, Check, Info } from 'lucide-react';
import { supabase, handleSupabaseError } from '@/lib/supabaseClient';
import { toast } from 'react-hot-toast';
import { useSettings } from '@/contexts/SettingsContext';

// Version information type
interface VersionInfo {
  version: string;
  buildDate: string;
  commit: string;
  environment: string;
  buildNumber?: string;
}

export function Settings() {
  const { settings, refreshSettings } = useSettings();
  const [minOrderAmount, setMinOrderAmountState] = useState('0');
  const [paymentMethodsText, setPaymentMethodsTextState] = useState('');
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [googleAnalyticsId, setGoogleAnalyticsIdState] = useState('');
  const [footerText, setFooterText] = useState('');
  const [footerDescription, setFooterDescription] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [instagramUrl, setInstagramUrl] = useState('https://instagram.com');
  const [facebookUrl, setFacebookUrl] = useState('https://facebook.com');
  const [hideInstagram, setHideInstagram] = useState(false);
  const [hideFacebook, setHideFacebook] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [error, setError] = useState('');
  const [migrationNeeded, setMigrationNeeded] = useState(false);
  const [versionInfo, setVersionInfo] = useState<VersionInfo | null>(null);
  const [isLoadingVersion, setIsLoadingVersion] = useState(false);
  const [orderConfirmationText, setOrderConfirmationTextState] = useState('');
  const { t } = useTranslation();
  
  // Payment method edit state
  const [editingMethod, setEditingMethod] = useState<PaymentMethod | null>(null);
  const [newMethod, setNewMethod] = useState<Partial<PaymentMethod>>({
    name: '',
    icon: '',
    enabled: true,
    sort_order: 0
  });
  const [showNewMethodForm, setShowNewMethodForm] = useState(false);

  useEffect(() => {
    let isMounted = true;
    
    async function fetchSettings() {
      try {
        // First check if payment_methods table exists
        const tableCheckResponse = await supabase
          .from('payment_methods')
          .select('*', { count: 'exact', head: true });
        
        // Set migration needed flag if table doesn't exist
        if (tableCheckResponse.error && 
            (tableCheckResponse.error.code === '404' || 
             tableCheckResponse.error.message?.includes('does not exist') ||
             tableCheckResponse.error.message?.includes('42P01'))) {
          setMigrationNeeded(true);
        }
        
        // Get footer text from app_settings
        const { data: footerData, error: footerError } = await supabase
          .rpc('get_setting', { p_key: 'footer_text' });
        
        const { data: footerDescData, error: footerDescError } = await supabase
          .rpc('get_setting', { p_key: 'footer_description' });

        // Get contact information
        const { data: emailData } = await supabase
          .rpc('get_setting', { p_key: 'contact_email' });
        
        const { data: phoneData } = await supabase
          .rpc('get_setting', { p_key: 'contact_phone' });
          
        // Get social media URLs
        const { data: instagramData } = await supabase
          .rpc('get_setting', { p_key: 'social_instagram' });
          
        const { data: facebookData } = await supabase
          .rpc('get_setting', { p_key: 'social_facebook' });
          
        // Get social media visibility settings
        const { data: hideInstagramData } = await supabase
          .rpc('get_setting', { p_key: 'hide_instagram' });
          
        const { data: hideFacebookData } = await supabase
          .rpc('get_setting', { p_key: 'hide_facebook' });

        if (isMounted) {
          setMinOrderAmountState(settings.minOrderAmount.toString());
          setPaymentMethodsTextState(settings.paymentMethodsText || '');
          setPaymentMethods(settings.paymentMethods || []);
          setGoogleAnalyticsIdState(settings.googleAnalyticsId || '');
          setOrderConfirmationTextState(settings.orderConfirmationText || '');
          if (!footerError && footerData) {
            setFooterText(footerData);
          }
          if (!footerDescError && footerDescData) {
            setFooterDescription(footerDescData);
          }
          if (emailData) setContactEmail(emailData);
          if (phoneData) setContactPhone(phoneData);
          if (instagramData) setInstagramUrl(instagramData);
          if (facebookData) setFacebookUrl(facebookData);
          if (hideInstagramData) setHideInstagram(hideInstagramData === 'true');
          if (hideFacebookData) setHideFacebook(hideFacebookData === 'true');
          setIsLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          setError('Failed to load settings. Using local data instead.');
          setIsLoading(false);
        }
      }
    }
    
    fetchSettings();
    fetchVersionInfo();
    
    return () => {
      isMounted = false;
    };
  }, [settings]);

  const fetchVersionInfo = async () => {
    setIsLoadingVersion(true);
    try {
      const response = await fetch('/version.txt');
      if (!response.ok) {
        throw new Error('Failed to fetch version information');
      }
      
      const text = await response.text();
      const lines = text.split('\n');
      
      const info: VersionInfo = {
        version: lines.find(line => line.startsWith('Version:'))?.split('Version:')[1]?.trim() || 'Unknown',
        buildDate: lines.find(line => line.startsWith('Build date:'))?.split('Build date:')[1]?.trim() || 'Unknown',
        commit: lines.find(line => line.startsWith('Commit:'))?.split('Commit:')[1]?.trim() || 'Unknown',
        environment: lines.find(line => line.startsWith('Environment:'))?.split('Environment:')[1]?.trim() || 'Unknown',
        buildNumber: lines.find(line => line.startsWith('Build number:'))?.split('Build number:')[1]?.trim()
      };
      
      setVersionInfo(info);
    } catch (err) {
      logger.error('Error fetching version info:', err);
      // Don't set an error state as this is not critical functionality
    } finally {
      setIsLoadingVersion(false);
    }
  };

  const handleOrderConfirmationTextSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      logger.debug('Saving order confirmation text:', orderConfirmationText);
      
      // Use the RPC method to save the setting
      const { error } = await supabase
        .rpc('set_setting', { 
          p_key: 'order_confirmation_text',
          p_value: orderConfirmationText,
          p_description: 'Text shown after an order is placed'
        });

      if (error) throw error;
      
      // Check if the setting was saved
      const { data: checkData, error: checkError } = await supabase
        .rpc('get_setting', { p_key: 'order_confirmation_text' });
      
      logger.debug('Verification after save:', { checkData, checkError });
      
      if (refreshSettings) {
        await refreshSettings();
      }
      
      toast.success('Order confirmation text updated successfully');
    } catch (err) {
      logger.error('Error updating order confirmation text:', err);
      toast.error('Failed to update order confirmation text');
    } finally {
      setIsSaving(false);
    }
  };

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setIsSaving(true);
    setSaveSuccess(false);
    setError('');
    
    try {
      // Convert to number to validate
      const numericValue = parseFloat(minOrderAmount);
      if (isNaN(numericValue) || numericValue < 0) {
        throw new Error('Please enter a valid amount (0 or greater)');
      }
      
      // Save minimum order amount
      const success = await setMinOrderAmount(numericValue);
      
      // Save payment methods text
      await setPaymentMethodsText(paymentMethodsText);
      
      if (!success) {
        throw new Error('Failed to save settings');
      }
      
      setSaveSuccess(true);
      
      // Reset success message after 3 seconds
      setTimeout(() => {
        setSaveSuccess(false);
      }, 3000);
    } catch (err: any) {
      logger.warn('Error in handleSubmit:', err.message);
      setError(err.message || 'An error occurred while saving settings. Using local storage as fallback.');
    } finally {
      setIsSaving(false);
    }
  }
  
  async function handleSavePaymentMethod(method: Partial<PaymentMethod> & { id?: number }) {
    setIsSaving(true);
    setError('');
    
    try {
      if (!method || !method.name) {
        throw new Error('Payment method name is required');
      }
      
      // Attempt to save the payment method
      await savePaymentMethod(method);
      
      // Refresh payment methods
      const settings = await getAppSettings();
      setPaymentMethods(settings.paymentMethods || []);
      
      // Reset form
      setEditingMethod(null);
      setShowNewMethodForm(false);
      setNewMethod({
        name: '',
        icon: '',
        enabled: true,
        sort_order: 0
      });
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      logger.warn('Error in handleSavePaymentMethod:', err.message);
      setError(err.message || 'An error occurred while saving payment method. Using local storage as fallback.');
      
      // Try to refresh payment methods anyway, as they might be in localStorage
      try {
        const settings = await getAppSettings();
        setPaymentMethods(settings.paymentMethods || []);
      } catch (refreshError) {
        logger.warn('Error refreshing payment methods:', refreshError);
      }
    } finally {
      setIsSaving(false);
    }
  }
  
  async function handleDeletePaymentMethod(id: number) {
    if (!window.confirm('Are you sure you want to delete this payment method?')) {
      return;
    }
    
    setIsSaving(true);
    setError('');
    
    try {
      // Attempt to delete the payment method
      await deletePaymentMethod(id);
      
      // Refresh payment methods
      const settings = await getAppSettings();
      setPaymentMethods(settings.paymentMethods || []);
      
      setSaveSuccess(true);
      setTimeout(() => setSaveSuccess(false), 3000);
    } catch (err: any) {
      logger.warn('Error in handleDeletePaymentMethod:', err.message);
      setError(err.message || 'An error occurred while deleting payment method. Using local storage as fallback.');
      
      // Try to refresh payment methods anyway, as they might be in localStorage
      try {
        const settings = await getAppSettings();
        setPaymentMethods(settings.paymentMethods || []);
      } catch (refreshError) {
        logger.warn('Error refreshing payment methods:', refreshError);
      }
    } finally {
      setIsSaving(false);
    }
  }
  
  function movePaymentMethod(index: number, direction: 'up' | 'down') {
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    
    if (newIndex < 0 || newIndex >= paymentMethods.length) {
      return;
    }
    
    const updatedMethods = [...paymentMethods];
    const method = updatedMethods[index];
    
    // Update sort orders
    const targetSortOrder = updatedMethods[newIndex].sort_order;
    method.sort_order = targetSortOrder;
    
    // Reorder array for display
    [updatedMethods[index], updatedMethods[newIndex]] = [updatedMethods[newIndex], updatedMethods[index]];
    
    setPaymentMethods(updatedMethods);
    
    // Save the updated sort order
    handleSavePaymentMethod(method);
  }

  const handleFooterTextSave = async () => {
    try {
      const { error: textError } = await supabase
        .rpc('set_setting', { 
          p_key: 'footer_text',
          p_value: footerText,
          p_description: 'Footer text displayed on the website'
        });

      const { error: descError } = await supabase
        .rpc('set_setting', {
          p_key: 'footer_description',
          p_value: footerDescription,
          p_description: 'Footer description displayed on the website'
        });

      if (textError) throw textError;
      if (descError) throw descError;

      toast.success('Footer text updated successfully');
    } catch (err) {
      logger.error('Error updating footer text:', err);
      toast.error('Failed to update footer text');
    }
  };

  const handleContactSave = async () => {
    try {
      const { error: emailError } = await supabase
        .rpc('set_setting', { 
          p_key: 'contact_email',
          p_value: contactEmail,
          p_description: 'Contact email displayed in footer'
        });

      const { error: phoneError } = await supabase
        .rpc('set_setting', {
          p_key: 'contact_phone',
          p_value: contactPhone,
          p_description: 'Contact phone number displayed in footer'
        });

      if (emailError) throw emailError;
      if (phoneError) throw phoneError;

      toast.success('Contact information updated successfully');
    } catch (err) {
      logger.error('Error updating contact information:', err);
      toast.error('Failed to update contact information');
    }
  };
  
  const handleSocialMediaSave = async () => {
    try {
      const { error: instagramError } = await supabase
        .rpc('set_setting', { 
          p_key: 'social_instagram',
          p_value: instagramUrl,
          p_description: 'Instagram URL displayed in footer'
        });

      const { error: facebookError } = await supabase
        .rpc('set_setting', {
          p_key: 'social_facebook',
          p_value: facebookUrl,
          p_description: 'Facebook URL displayed in footer'
        });
        
      const { error: hideInstagramError } = await supabase
        .rpc('set_setting', {
          p_key: 'hide_instagram',
          p_value: hideInstagram.toString(),
          p_description: 'Whether to hide Instagram icon in footer'
        });
        
      const { error: hideFacebookError } = await supabase
        .rpc('set_setting', {
          p_key: 'hide_facebook',
          p_value: hideFacebook.toString(),
          p_description: 'Whether to hide Facebook icon in footer'
        });

      if (instagramError) throw instagramError;
      if (facebookError) throw facebookError;
      if (hideInstagramError) throw hideInstagramError;
      if (hideFacebookError) throw hideFacebookError;

      toast.success('Social media settings updated successfully');
    } catch (err) {
      logger.error('Error updating social media links:', err);
      toast.error('Failed to update social media settings');
    }
  };

  const handleAnalyticsSave = async () => {
    try {
      await setGoogleAnalyticsId(googleAnalyticsId);
      toast.success('Google Analytics ID updated successfully');
    } catch (err) {
      logger.error('Error updating Google Analytics ID:', err);
      toast.error('Failed to update Google Analytics ID');
    }
  };

  const handleChangePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsChangingPassword(true);
    setPasswordError('');
    setPasswordSuccess(false);

    // Validate passwords
    if (newPassword.length < 8) {
      setPasswordError('Det nya lösenordet måste vara minst 8 tecken långt');
      setIsChangingPassword(false);
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('Lösenorden matchar inte');
      setIsChangingPassword(false);
      return;
    }

    try {
      // Update the password
      const { error: updateError } = await supabase.auth.updateUser({
        password: newPassword
      });

      if (updateError) throw updateError;

      setPasswordSuccess(true);
      setNewPassword('');
      setConfirmPassword('');
      
      // Reset success message after 3 seconds
      setTimeout(() => {
        setPasswordSuccess(false);
      }, 3000);
      
      toast.success('Lösenordet har uppdaterats');
    } catch (err: any) {
      logger.error('Error changing password:', err);
      setPasswordError(err.message || 'Det gick inte att uppdatera lösenordet');
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
      <div className="space-y-6 sm:px-6 lg:px-0 lg:col-span-9">
        <h1 className="text-2xl font-semibold text-gray-900 mb-6">{t('admin.settings')}</h1>
        
        {isLoading ? (
          <div className="flex justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-green-500"></div>
          </div>
        ) : (
          <div className="space-y-8">
            {migrationNeeded && (
              <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-yellow-700">
                      <strong>Database migration needed.</strong> Some settings are being stored locally until migrations are applied.
                      <a href="#" className="font-medium underline text-yellow-700 hover:text-yellow-600 ml-1">
                        Read more
                      </a>
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Order Confirmation Text Section */}
            <form onSubmit={handleOrderConfirmationTextSubmit}>
              <div className="shadow sm:rounded-md sm:overflow-hidden">
                <div className="bg-white py-6 px-4 space-y-6 sm:p-6">
                  <div>
                    <h2 id="order-confirmation-heading" className="text-lg leading-6 font-medium text-gray-900">
                      Order Confirmation Text
                    </h2>
                    <p className="mt-1 text-sm text-gray-500">
                      This text will be shown to customers after they place an order.
                    </p>
                  </div>

                  <div>
                    <label htmlFor="order-confirmation" className="block text-sm font-medium text-gray-700">
                      Confirmation Message
                    </label>
                    <div className="mt-1">
                      <textarea
                        id="order-confirmation"
                        name="order-confirmation"
                        rows={4}
                        className="shadow-sm focus:ring-green-500 focus:border-green-500 block w-full sm:text-sm border-gray-300 rounded-md"
                        value={orderConfirmationText}
                        onChange={(e) => setOrderConfirmationTextState(e.target.value)}
                      />
                    </div>
                  </div>
                </div>
                <div className="px-4 py-3 bg-gray-50 text-right sm:px-6">
                  <button
                    type="submit"
                    disabled={isSaving}
                    className="bg-green-600 border border-transparent rounded-md shadow-sm py-2 px-4 inline-flex justify-center text-sm font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    {isSaving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              </div>
            </form>

            {/* Build Version Information */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4 flex items-center">
                <Info className="inline-block w-5 h-5 mr-2" />
                System Information
              </h2>
              <div className="space-y-3">
                {isLoadingVersion ? (
                  <div className="flex items-center text-sm text-gray-500">
                    <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-gray-500 mr-2"></div>
                    Loading version information...
                  </div>
                ) : versionInfo ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <div className="text-sm font-medium text-gray-500">App Version</div>
                      <div className="text-sm text-gray-900">{versionInfo.version}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500">Build Date</div>
                      <div className="text-sm text-gray-900">{versionInfo.buildDate}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500">Commit</div>
                      <div className="text-sm text-gray-900">{versionInfo.commit}</div>
                    </div>
                    <div>
                      <div className="text-sm font-medium text-gray-500">Environment</div>
                      <div className="text-sm text-gray-900">{versionInfo.environment}</div>
                    </div>
                    {versionInfo.buildNumber && (
                      <div>
                        <div className="text-sm font-medium text-gray-500">Build Number</div>
                        <div className="text-sm text-gray-900">{versionInfo.buildNumber}</div>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-sm text-gray-500">
                    Version information not available
                  </div>
                )}
              </div>
            </div>

            {/* Footer Text Settings */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Footer Text</h2>
              <div className="space-y-4">
                <div>
                  <label htmlFor="footerText" className="block text-sm font-medium text-gray-700">
                    Title
                  </label>
                  <input
                    type="text"
                    id="footerText"
                    value={footerText}
                    onChange={(e) => setFooterText(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                  />
                </div>
                <div>
                  <label htmlFor="footerDescription" className="block text-sm font-medium text-gray-700">
                    Description
                  </label>
                  <input
                    type="text"
                    id="footerDescription"
                    value={footerDescription}
                    onChange={(e) => setFooterDescription(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleFooterTextSave}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    Save Footer Text
                  </button>
                </div>
              </div>
            </div>

            {/* Contact Information Settings */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Contact Information</h2>
              <div className="space-y-4">
                <div>
                  <label htmlFor="contactEmail" className="block text-sm font-medium text-gray-700">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="contactEmail"
                    value={contactEmail}
                    onChange={(e) => setContactEmail(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                    placeholder="info@lokalaproducenter.se"
                  />
                </div>
                <div>
                  <label htmlFor="contactPhone" className="block text-sm font-medium text-gray-700">
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    id="contactPhone"
                    value={contactPhone}
                    onChange={(e) => setContactPhone(e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                    placeholder="+46 123 456 789"
                  />
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleContactSave}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    Save Contact Information
                  </button>
                </div>
              </div>
            </div>
            
            {/* Social Media Links Settings */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Följ oss (Social Media)</h2>
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="instagramUrl" className="block text-sm font-medium text-gray-700">
                      Instagram URL
                    </label>
                    <input
                      type="url"
                      id="instagramUrl"
                      value={instagramUrl}
                      onChange={(e) => setInstagramUrl(e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                      placeholder="https://instagram.com/yourprofile"
                    />
                    <div className="mt-2 flex items-center">
                      <input
                        type="checkbox"
                        id="hideInstagram"
                        checked={hideInstagram}
                        onChange={(e) => setHideInstagram(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <label htmlFor="hideInstagram" className="ml-2 text-sm text-gray-700">
                        Hide Instagram icon
                      </label>
                    </div>
                  </div>
                  <div>
                    <label htmlFor="facebookUrl" className="block text-sm font-medium text-gray-700">
                      Facebook URL
                    </label>
                    <input
                      type="url"
                      id="facebookUrl"
                      value={facebookUrl}
                      onChange={(e) => setFacebookUrl(e.target.value)}
                      className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                      placeholder="https://facebook.com/yourpage"
                    />
                    <div className="mt-2 flex items-center">
                      <input
                        type="checkbox"
                        id="hideFacebook"
                        checked={hideFacebook}
                        onChange={(e) => setHideFacebook(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <label htmlFor="hideFacebook" className="ml-2 text-sm text-gray-700">
                        Hide Facebook icon
                      </label>
                    </div>
                  </div>
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleSocialMediaSave}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    Save Social Media Settings
                  </button>
                </div>
              </div>
            </div>

            {/* Google Analytics Settings */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Google Analytics</h2>
              <div className="space-y-4">
                <div>
                  <label htmlFor="googleAnalyticsId" className="block text-sm font-medium text-gray-700">
                    Google Analytics Measurement ID
                  </label>
                  <div className="mt-1 flex rounded-md shadow-sm">
                    <input
                      type="text"
                      id="googleAnalyticsId"
                      value={googleAnalyticsId}
                      onChange={(e) => setGoogleAnalyticsIdState(e.target.value)}
                      className="flex-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                      placeholder="G-XXXXXXXXXX"
                    />
                  </div>
                  <p className="mt-2 text-sm text-gray-500">
                    Enter your Google Analytics 4 Measurement ID (starting with G-). Leave empty to disable analytics.
                  </p>
                </div>
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleAnalyticsSave}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    Save Analytics Settings
                  </button>
                </div>
              </div>
            </div>

            {/* Admin Password Settings */}
            <div className="bg-white shadow rounded-lg p-6">
              <h2 className="text-lg font-medium text-gray-900 mb-4">Administratörlösenord</h2>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
                    Nytt lösenord
                  </label>
                  <input
                    type="password"
                    id="newPassword"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    required
                    minLength={8}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                  />
                  <p className="mt-1 text-sm text-gray-500">
                    Lösenordet måste vara minst 8 tecken långt
                  </p>
                </div>
                <div>
                  <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                    Bekräfta nytt lösenord
                  </label>
                  <input
                    type="password"
                    id="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-green-500 focus:ring-green-500 sm:text-sm"
                  />
                </div>
                
                {passwordError && (
                  <div className="text-sm text-red-600">
                    {passwordError}
                  </div>
                )}
                
                {passwordSuccess && (
                  <div className="text-sm text-green-600">
                    Lösenordet har uppdaterats framgångsrikt!
                  </div>
                )}
                
                <div className="flex justify-end">
                  <button
                    type="submit"
                    disabled={isChangingPassword}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isChangingPassword ? 'Uppdaterar...' : 'Uppdatera lösenord'}
                  </button>
                </div>
              </form>
            </div>

            <div className="bg-white overflow-hidden shadow rounded-lg">
              <div className="px-4 py-5 sm:p-6">
                <div className="flex justify-between items-center mb-4">
                  <h2 className="text-lg leading-6 font-medium text-gray-900">
                    Payment Methods
                  </h2>
                  <button
                    type="button"
                    onClick={() => setShowNewMethodForm(true)}
                    className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                  >
                    <PlusCircle className="h-4 w-4 mr-1" />
                    Add Method
                  </button>
                </div>
                
                {/* New Payment Method Form */}
                {showNewMethodForm && (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-md font-medium">Add New Payment Method</h3>
                      <button
                        type="button"
                        onClick={() => setShowNewMethodForm(false)}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label htmlFor="newMethodName" className="block text-sm font-medium text-gray-700 mb-1">
                          Name
                        </label>
                        <input
                          type="text"
                          id="newMethodName"
                          className="focus:ring-green-500 focus:border-green-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          value={newMethod.name}
                          onChange={(e) => setNewMethod({...newMethod, name: e.target.value})}
                        />
                      </div>
                      <div>
                        <label htmlFor="newMethodIcon" className="block text-sm font-medium text-gray-700 mb-1">
                          Icon URL
                        </label>
                        <input
                          type="text"
                          id="newMethodIcon"
                          className="focus:ring-green-500 focus:border-green-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          value={newMethod.icon || ''}
                          onChange={(e) => setNewMethod({...newMethod, icon: e.target.value})}
                        />
                      </div>
                    </div>
                    
                    <div className="flex items-center mb-4">
                      <input
                        type="checkbox"
                        id="newMethodEnabled"
                        checked={newMethod.enabled}
                        onChange={(e) => setNewMethod({...newMethod, enabled: e.target.checked})}
                        className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <label htmlFor="newMethodEnabled" className="ml-2 text-sm text-gray-700">
                        Enabled
                      </label>
                    </div>
                    
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => handleSavePaymentMethod(newMethod)}
                        disabled={!newMethod.name || isSaving}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSaving ? 'Saving...' : 'Save Method'}
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Edit Payment Method Form */}
                {editingMethod && (
                  <div className="mb-6 p-4 bg-gray-50 rounded-lg">
                    <div className="flex justify-between items-center mb-3">
                      <h3 className="text-md font-medium">Edit Payment Method</h3>
                      <button
                        type="button"
                        onClick={() => setEditingMethod(null)}
                        className="text-gray-400 hover:text-gray-500"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-4 mb-4">
                      <div>
                        <label htmlFor="editMethodName" className="block text-sm font-medium text-gray-700 mb-1">
                          Name
                        </label>
                        <input
                          type="text"
                          id="editMethodName"
                          className="focus:ring-green-500 focus:border-green-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          value={editingMethod.name}
                          onChange={(e) => setEditingMethod({...editingMethod, name: e.target.value})}
                        />
                      </div>
                      <div>
                        <label htmlFor="editMethodIcon" className="block text-sm font-medium text-gray-700 mb-1">
                          Icon URL
                        </label>
                        <input
                          type="text"
                          id="editMethodIcon"
                          className="focus:ring-green-500 focus:border-green-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          value={editingMethod.icon || ''}
                          onChange={(e) => setEditingMethod({...editingMethod, icon: e.target.value})}
                        />
                      </div>
                    </div>
                    
                    <div className="flex items-center mb-4">
                      <input
                        type="checkbox"
                        id="editMethodEnabled"
                        checked={editingMethod.enabled}
                        onChange={(e) => setEditingMethod({...editingMethod, enabled: e.target.checked})}
                        className="h-4 w-4 rounded border-gray-300 text-green-600 focus:ring-green-500"
                      />
                      <label htmlFor="editMethodEnabled" className="ml-2 text-sm text-gray-700">
                        Enabled
                      </label>
                    </div>
                    
                    <div className="flex justify-end">
                      <button
                        type="button"
                        onClick={() => handleSavePaymentMethod(editingMethod)}
                        disabled={!editingMethod.name || isSaving}
                        className="inline-flex items-center px-3 py-1.5 border border-transparent text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSaving ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Payment Methods List */}
                {paymentMethods.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No payment methods configured yet.</p>
                ) : (
                  <ul className="divide-y divide-gray-200">
                    {paymentMethods.map((method, index) => (
                      <li key={method.id} className="py-3">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center">
                            {method.icon && (
                              <div className="h-10 w-10 mr-3 flex-shrink-0">
                                <img 
                                  src={method.icon} 
                                  alt={method.name}
                                  className="h-10 w-10 object-contain" 
                                  onError={(e) => (e.target as HTMLImageElement).style.display = 'none'}
                                />
                              </div>
                            )}
                            <div>
                              <p className="text-sm font-medium text-gray-900">{method.name}</p>
                              <div className="flex items-center mt-1">
                                <div className={`h-2 w-2 rounded-full mr-1 ${method.enabled ? 'bg-green-500' : 'bg-gray-300'}`}></div>
                                <p className="text-xs text-gray-500">{method.enabled ? 'Enabled' : 'Disabled'}</p>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <button
                              type="button"
                              onClick={() => movePaymentMethod(index, 'up')}
                              disabled={index === 0}
                              className="text-gray-400 hover:text-gray-500 disabled:opacity-30"
                            >
                              <MoveUp className="h-5 w-5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => movePaymentMethod(index, 'down')}
                              disabled={index === paymentMethods.length - 1}
                              className="text-gray-400 hover:text-gray-500 disabled:opacity-30"
                            >
                              <MoveDown className="h-5 w-5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingMethod(method)}
                              className="text-indigo-600 hover:text-indigo-900"
                            >
                              Edit
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDeletePaymentMethod(method.id)}
                              className="text-red-600 hover:text-red-900"
                            >
                              <Trash2 className="h-5 w-5" />
                            </button>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 